import { useAchievementTypes, useReduxState } from 'hooks';
import { AchievementIcon } from 'components/achievements';
import { LinkButton } from 'components/common';
import { CardExtension, CardProgressStatus } from 'components/common/tasks';
import { PathStepTileContent, PathStepTilePicture } from '.';

import type {
  AchievementProgress,
  PathStepItemAchievement
} from 'shared-types';

interface Props {
  achievement: PathStepItemAchievement['achievement'] & {
    progressData: AchievementProgress;
  };
  achievementIsAvailable?: boolean;
  stepAvailable?: boolean;
  stepEnabled?: boolean;
}

const PathStepTileAchievement = ({
  achievement,
  achievementIsAvailable,
  stepAvailable,
  stepEnabled
}: Props) => {
  const { messages, theme, user } = useReduxState();

  const achievementTypes = useAchievementTypes();

  const progressData = achievement.progressData;
  const completedProgressItems =
    progressData?.numberOfCompletedTemplateItems || 0;
  const totalProgressItems = progressData?.template.length || 0;
  const progressProps = progressData && {
    progress: totalProgressItems
      ? completedProgressItems / totalProgressItems
      : 0,
    progressValueLabel: `${completedProgressItems}/${totalProgressItems}`
  };

  return (
    <div className="achievement">
      <PathStepTilePicture icon={achievementTypes[achievement.type].icon}>
        <div className="header">
          <AchievementIcon size="large" type={achievement.type} />
        </div>
      </PathStepTilePicture>

      <PathStepTileContent
        description={achievement.description}
        subtitle={achievementTypes[achievement.type].title}
        title={achievement.title}
      >
        <div className="footer">
          <div className="footer-content">
            <div className="status">
              {achievement.progressData &&
                (achievement.progressData.status === 'completed' ? (
                  <CardProgressStatus
                    category="success"
                    icon="check"
                    label={messages.commonCompleted}
                  />
                ) : achievement.type === 'conversation' ||
                  achievement.type === 'tasklist' ? (
                  <CardProgressStatus
                    label={
                      achievement.progressData.status === 'inprogress'
                        ? messages.commonInProgress
                        : messages.commonNotStarted
                    }
                    {...progressProps}
                    progressShowIfZero={true}
                  />
                ) : achievementIsAvailable ? (
                  <CardProgressStatus
                    category="default"
                    icon="book-open"
                    label={messages.commonAvailable}
                  />
                ) : (
                  <CardProgressStatus
                    category="default"
                    icon="minus"
                    label={messages.tasksUsersNotCompletedSingular}
                  />
                ))}
            </div>

            {(stepAvailable && stepEnabled) ||
            user?.roles.includes('superadmin') ? (
              <LinkButton
                href={`/achievements/${achievement.id}`}
                isRounded={true}
                onClick={(ev) => ev.stopPropagation()}
                size="small"
                style={{ fontSize: '1.3rem', padding: '6px 12px 7px' }}
                text={messages.commonOpen}
              />
            ) : null}
          </div>
        </div>
      </PathStepTileContent>

      {/* display notice if admin has to mark achievement to be completed */}
      {stepAvailable &&
      !achievementIsAvailable &&
      achievement.progressData?.status !== 'completed' &&
      achievement.itemsMarkableByUser !== 'all' ? (
        <CardExtension
          icon="exclamation-circle"
          innerStyle={{ color: theme.textColorLight }}
        >
          {achievement.itemsMarkableByUser === 'some'
            ? messages.pathsStepsRequiresAdminActionMaybe
            : messages.pathsStepsRequiresAdminAction}
        </CardExtension>
      ) : null}

      <style jsx>{`
        .header {
          align-items: center;
          display: flex;
          height: 100%;
          justify-content: center;
        }
        .footer {
          margin-top: auto;
          padding-top: 20px;
        }
        .footer-content {
          display: flex;
          justify-content: space-between;
          width: 100%;
        }
        .status {
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default PathStepTileAchievement;
